import { hasCircleValue, isCircled, toggleCircleOption } from "./circleValue";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Rnd } from "react-rnd";
import {
  HandRaisedIcon,
  CursorArrowRaysIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MicrophoneIcon,
  ArrowUpTrayIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";
import Tooltip from "../common/Tooltip";
import { FormTemplate } from "../../db/schema";
import { resolveFormPdfUrl } from "../../services/pdfService";
import { useFormDesigner } from "../../context/FormDesignerContext";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import { usePdfViewer } from "../../hooks/usePdfViewer";
import { useFieldMovement } from "../../hooks/useFieldMovement";
import { useMarqueeSelection } from "../../hooks/useMarqueeSelection";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import { useFieldAlignment } from "../../hooks/useFieldAlignment";
import { useSnapGuides } from "../../hooks/useSnapGuides";
import SnapGuideLines from "../common/SnapGuideLines";
import PdfFloatingNav from "./PdfFloatingNav";
import ResizeHandle from "../common/ResizeHandle";
import { useResizablePanel } from "../../hooks/useResizablePanel";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const FIELD_TYPE_LABELS: Record<string, string> = {
  text: "Text",
  textarea: "Text Area",
  checkbox: "Checkbox",
  circle: "Circle",
};

// Each entry in fieldValues contains both field definition and value
interface FieldEntry {
  id: string;
  name?: string;
  label: string;
  type: string;
  rect: { x: number; y: number; width: number; height: number };
  page?: number;
  section?: string;
  required?: boolean;
  fontSize?: number;
  lineHeight?: number;
  additionalContext?: string;
  options?: string[];
  optionRects?: { x: number; y: number; width: number; height: number }[];
  multiple?: boolean;
  value: any;
  confidence?: number;
  [key: string]: any;
}

export interface FormFillerProps {
  template: FormTemplate;
  fieldValues: Record<string, FieldEntry>;
  onSaveValues?: (values: Record<string, FieldEntry>) => void | Promise<void>;
  onExportPdf?: (values: Record<string, FieldEntry>) => void;
  onCancel: () => void;
  // Record or upload more audio for this already-filled form. Unsaved edits
  // are saved first, so the new fill builds on them.
  onAddMore?: (how: "record" | "upload") => void;
  // An addition is being transcribed and filled: editing waits for it.
  addingMore?: boolean;
  onCancelAddMore?: () => void;
}

const FormFiller: React.FC<FormFillerProps> = ({
  template,
  fieldValues: initialValues,
  onSaveValues,
  onExportPdf,
  onCancel,
  onAddMore,
  addingMore = false,
  onCancelAddMore,
}) => {
  const { setIsDesignerOpen } = useFormDesigner();

  useEffect(() => {
    setIsDesignerOpen(true);
    return () => setIsDesignerOpen(false);
  }, [setIsDesignerOpen]);

  const [pdfFileUrl, setPdfFileUrl] = useState<string>("");
  useEffect(() => {
    let cancelled = false;
    resolveFormPdfUrl(template.original_pdf_url).then((u) => {
      if (!cancelled) setPdfFileUrl(u);
    });
    return () => {
      cancelled = true;
    };
  }, [template.original_pdf_url]);

  // Field state with undo/redo
  const { state: fieldValues, setState: setFieldValues, undo, redo, canUndo, canRedo } =
    useUndoRedo<Record<string, FieldEntry>>(initialValues || {});
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);

  // Track unsaved changes, guard navigation, handle cancel
  const { hasChanges, markSaved, handleCancel } = useUnsavedChanges({
    currentState: fieldValues,
    initialState: initialValues || {},
    onCancel,
  });

  // Tool state
  const [selectedTool, setSelectedTool] = useState<string>("select");
  const [showConfidence, setShowConfidence] = useState(
    () => localStorage.getItem("formDefaults:showConfidence") !== "false",
  );

  // PDF viewer (zoom, pan, page nav, space-to-pan)
  const pdf = usePdfViewer({
    storageKey: "formDesignerZoom",
    selectedTool,
  });

  // Destructure commonly used pdf viewer values
  const { scale } = pdf;

  // Derived field lists
  const allFields = useMemo(() => Object.values(fieldValues), [fieldValues]);
  const currentPageFields = useMemo(
    () => allFields.filter((f) => (f.page || 1) === pdf.currentPage),
    [allFields, pdf.currentPage],
  );

  // Marquee selection
  const marquee = useMarqueeSelection({
    canvasRef: pdf.canvasRef,
    scale: pdf.scale,
    getCanvasCoords: pdf.getCanvasCoords,
    getCurrentPageFields: useCallback(
      () => currentPageFields.map((f) => ({ id: f.id, rect: f.rect })),
      [currentPageFields],
    ),
  });
  const { selectedFieldIds, setSelectedFieldIds } = marquee;

  // Clear selection on page change
  useEffect(() => {
    marquee.clearSelection();
    setEditingFieldId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdf.currentPage]);

  // Shared field rect callbacks for movement + alignment hooks
  const getFieldRect = useCallback(
    (fieldId: string) => {
      const f = fieldValues[fieldId];
      return f ? f.rect : null;
    },
    [fieldValues],
  );
  const onMoveFields = useCallback(
    (updates: Array<{ id: string; rect: { x: number; y: number; width: number; height: number } }>) => {
      setFieldValues((prev) => {
        const next = { ...prev };
        for (const { id, rect } of updates) {
          if (next[id]) next[id] = { ...next[id], rect };
        }
        return next;
      });
    },
    [setFieldValues],
  );

  // Figma-style alignment snapping with live guide lines. The selection is
  // excluded from targets so group drags don't snap to co-moving fields.
  const snap = useSnapGuides({
    getTargetRects: useCallback(
      () => currentPageFields.map((f) => ({ id: f.id, rect: f.rect })),
      [currentPageFields],
    ),
    getExcludedIds: useCallback(() => selectedFieldIds, [selectedFieldIds]),
    pageWidth: pdf.pdfDimensions.width,
    pageHeight: pdf.pdfDimensions.height,
    scale,
  });

  // Field movement (drag + resize) via shared hook
  const fieldMovement = useFieldMovement({
    scale,
    getFieldRect,
    onMoveFields,
    onResizeField: useCallback(
      (fieldId, rect) => {
        setFieldValues((prev) => ({
          ...prev,
          [fieldId]: { ...prev[fieldId], rect },
        }));
      },
      [setFieldValues],
    ),
    selectedFieldIds,
    snapRect: snap.snapRect,
    onLiveDrag: snap.previewSnap,
    onDragEnd: snap.clearGuides,
    snapResizeRect: snap.snapResizeRect,
    onLiveResize: snap.previewResizeSnap,
  });

  // Field alignment for multi-select
  const alignment = useFieldAlignment({
    getFieldRect,
    onMoveFields,
    selectedFieldIds,
  });

  // Track drag state for checkboxes (click vs drag) and cursor
  const wasDraggedRef = useRef(false);
  const [isDraggingField, setIsDraggingField] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const sidebarPanel = useResizablePanel({
    storageKey: "pnai-formFillerSidebarWidth",
    defaultWidth: 288,
    minWidth: 240,
    maxWidth: 640,
    side: "left",
    maxViewportFraction: 0.5,
  });

  // Keyboard shortcuts (space-to-pan handled by usePdfViewer)
  useEffect(() => {
    const isInputFocused = () => {
      const el = document.activeElement;
      return (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement
      );
    };

    const onKeyDown = (e: KeyboardEvent) => {
      // Undo/Redo
      if ((e.metaKey || e.ctrlKey) && e.key === "z") {
        if (isInputFocused()) return;
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
        return;
      }

      // Ctrl/Cmd+A select all on current page
      if (
        (e.metaKey || e.ctrlKey) &&
        e.key === "a" &&
        selectedTool === "select" &&
        !isInputFocused()
      ) {
        e.preventDefault();
        marquee.selectAll();
        return;
      }

      // Escape clears selection
      if (e.key === "Escape") {
        marquee.clearSelection();
        setEditingFieldId(null);
        setSelectedTool("select");
        return;
      }

      // Delete/Backspace deletes all selected fields
      if (
        (e.key === "Delete" || e.key === "Backspace") &&
        selectedFieldIds.size > 0 &&
        !isInputFocused()
      ) {
        e.preventDefault();
        setFieldValues((prev) => {
          const next = { ...prev };
          selectedFieldIds.forEach((id) => delete next[id]);
          return next;
        });
        marquee.clearSelection();
        setEditingFieldId(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedFieldIds, selectedTool, setFieldValues, undo, redo, marquee]);

  // Container mouse down — pan or marquee
  const handleContainerMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Pan check (handles button check, .react-draggable skip, space/pan tool)
      if (pdf.handleContainerMouseDown(e)) return;

      // Select mode: start marquee
      if (selectedTool === "select") {
        marquee.startMarquee(e);
        return;
      }
    },
    [selectedTool, pdf, marquee],
  );

  // Canvas mousedown — place new field immediately on click
  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (selectedTool === "text" || selectedTool === "checkbox") {
        e.stopPropagation();
        const coords = pdf.getCanvasCoords(e);

        const newId = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        const isCheckbox = selectedTool === "checkbox";
        const storedCheckboxSize = parseInt(localStorage.getItem("formDefaults:checkboxSize") || "") || 12;
        const storedFontSize = parseInt(localStorage.getItem("formDefaults:fontSize") || "") || 10;
        const storedLineHeight = parseFloat(localStorage.getItem("formDefaults:lineHeight") || "") || 1.2;
        const storedMultiline = localStorage.getItem("formDefaults:multiline");
        const isMultiline = storedMultiline === null ? true : storedMultiline === "true";
        const fieldType = isCheckbox ? "checkbox" : (isMultiline ? "textarea" : "text");
        const newField: FieldEntry = {
          id: newId,
          label: isCheckbox ? "Checkbox" : "Text",
          type: fieldType,
          page: pdf.currentPage,
          rect: {
            x: coords.x,
            y: coords.y,
            width: isCheckbox ? storedCheckboxSize : 150,
            height: isCheckbox ? storedCheckboxSize : 20,
          },
          value: isCheckbox ? false : "",
          ...(isCheckbox ? {} : { fontSize: storedFontSize }),
          ...(fieldType === "textarea" ? { lineHeight: storedLineHeight } : {}),
        };

        setFieldValues((prev) => ({ ...prev, [newId]: newField }));
        setSelectedFieldIds(new Set([newId]));
        setSelectedTool("select");
        return;
      }
    },
    [selectedTool, pdf, setFieldValues, setSelectedFieldIds],
  );

  // Canvas click — clear editing state
  const handleCanvasClick = useCallback(() => {
    setEditingFieldId(null);
  }, []);

  // Delete field
  const handleDeleteField = useCallback((fieldId: string) => {
    setFieldValues((prev) => {
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
    marquee.clearSelection();
    setEditingFieldId(null);
  }, [setFieldValues, marquee]);

  // Update field value
  const updateValue = useCallback((fieldId: string, value: any) => {
    setFieldValues((prev) => ({
      ...prev,
      [fieldId]: { ...prev[fieldId], value },
    }));
  }, [setFieldValues]);

  const startAddMore = async (how: "record" | "upload") => {
    (document.activeElement as HTMLElement | null)?.blur(); // close the dropdown
    if (hasChanges && onSaveValues) {
      await onSaveValues(fieldValues);
      markSaved();
    }
    onAddMore?.(how);
  };

  // Derived
  const primarySelectedId = selectedFieldIds.size === 1 ? Array.from(selectedFieldIds)[0] : null;
  const selectedField = primarySelectedId ? fieldValues[primarySelectedId] : null;
  const filledCount = allFields.filter(
    (f) => f.value !== undefined && f.value !== null && f.value !== "" && f.value !== false,
  ).length;

  return (
    <div className="flex-1 min-w-0 h-screen flex flex-col bg-base-200">
      {/* Header */}
      <div className="bg-base-100 border-b border-base-300 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="secondary" size="sm" onClick={handleCancel}>
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            {hasChanges ? "Cancel" : "Back"}
          </Button>
          <div>
            <h2 className="font-bold">Form Editor</h2>
            <p className="text-xs text-base-content/60">
              Review and edit AI-filled values, then export
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-sm text-base-content/60">
            {filledCount} / {allFields.length} fields filled
          </span>
          <button
            onClick={undo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
            className="p-1 rounded hover:bg-base-200 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity duration-200"
          >
            <ArrowUturnLeftIcon className="h-5 w-5" />
          </button>
          <button
            onClick={redo}
            disabled={!canRedo}
            title="Redo (Ctrl+Shift+Z)"
            className="p-1 rounded hover:bg-base-200 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity duration-200"
          >
            <ArrowUturnRightIcon className="h-5 w-5" />
          </button>
          {onAddMore && (
            <div className="dropdown dropdown-end">
              <Button tabIndex={0} variant="secondary" disabled={addingMore}>
                <PlusCircleIcon className="h-4 w-4 mr-1" />
                Add to form
              </Button>
              <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 rounded-lg z-[40] w-56 p-2 shadow mt-2 border border-base-300"
              >
                <li>
                  <button onClick={() => void startAddMore("record")} className="hover:bg-gray-100 dark:hover:bg-base-200">
                    <MicrophoneIcon className="size-4" />
                    Record Audio
                  </button>
                </li>
                <li>
                  <button onClick={() => void startAddMore("upload")} className="hover:bg-gray-100 dark:hover:bg-base-200">
                    <ArrowUpTrayIcon className="size-4" />
                    Upload Audio
                  </button>
                </li>
              </ul>
            </div>
          )}
          {onSaveValues && (
            <Button variant="secondary" disabled={!hasChanges || addingMore} onClick={async () => {
              await onSaveValues(fieldValues);
              markSaved();
            }}>
              Save
            </Button>
          )}
          {onExportPdf && (
            <Button variant="primary" disabled={addingMore} onClick={() => onExportPdf(fieldValues)}>
              Export PDF
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden relative">
        {addingMore && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-base-200/80 backdrop-blur-sm">
            <div className="max-w-sm rounded-xl border border-base-300 bg-base-100 p-6 text-center shadow-lg">
              <span className="loading loading-spinner loading-md text-blue-500" />
              <p className="mt-3 font-semibold text-base-content">Adding to the form…</p>
              <p className="mt-1 text-sm text-base-content/70">
                Reading the new recording and filling in what it adds. Nothing already on the form is
                cleared. You can leave this page.
              </p>
              {onCancelAddMore && (
                <button onClick={onCancelAddMore} className="mt-4 text-sm text-base-content/60 hover:underline">
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}
        {/* Toolbar */}
        <div className="w-44 flex-shrink-0 bg-base-100 border-r border-base-300 flex flex-col py-3 gap-0.5 px-2">
          <Tooltip overlay="Select fields" placement="right">
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setSelectedTool("select")}
              className={cn(
                "btn border-0 w-full justify-start !min-h-10 shadow-none h-[24px]",
                selectedTool === "select"
                  ? "bg-blue-50 dark:bg-blue-900/40 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                  : "hover:bg-base-300 text-gray-700 dark:text-base-content/80",
              )}
            >
              <div className="flex items-center pl-1">
                <CursorArrowRaysIcon className="h-5 w-5 mr-2" />
                <span className="text-sm">Select</span>
              </div>
            </button>
          </Tooltip>
          <Tooltip overlay="Click and drag to pan" placement="right">
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setSelectedTool("pan");
                marquee.clearSelection();
              }}
              className={cn(
                "btn border-0 w-full justify-start !min-h-10 shadow-none h-[24px]",
                selectedTool === "pan"
                  ? "bg-blue-50 dark:bg-blue-900/40 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                  : "hover:bg-base-300 text-gray-700 dark:text-base-content/80",
              )}
            >
              <div className="flex items-center pl-1">
                <HandRaisedIcon className="h-5 w-5 mr-2" />
                <span className="text-sm">Pan</span>
              </div>
            </button>
          </Tooltip>

          <div className="my-2 border-t border-base-300" />

          <Tooltip overlay="Click on PDF to add text field" placement="right">
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setSelectedTool("text")}
              className={cn(
                "btn border-0 w-full justify-start !min-h-10 shadow-none h-[24px]",
                selectedTool === "text"
                  ? "bg-blue-50 dark:bg-blue-900/40 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                  : "hover:bg-base-300 text-gray-700 dark:text-base-content/80",
              )}
            >
              <div className="flex items-center pl-1">
                <PlusIcon className="h-5 w-5 mr-2" />
                <span className="text-sm">Add Text</span>
              </div>
            </button>
          </Tooltip>
          <Tooltip overlay="Click on PDF to add checkbox" placement="right">
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setSelectedTool("checkbox")}
              className={cn(
                "btn border-0 w-full justify-start !min-h-10 shadow-none h-[24px]",
                selectedTool === "checkbox"
                  ? "bg-blue-50 dark:bg-blue-900/40 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                  : "hover:bg-base-300 text-gray-700 dark:text-base-content/80",
              )}
            >
              <div className="flex items-center pl-1">
                <CheckIcon className="h-5 w-5 mr-2" />
                <span className="text-sm">Checkbox</span>
              </div>
            </button>
          </Tooltip>

          <div className="mt-auto px-2 pt-3 border-t border-base-300 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-3.5 h-3.5 text-blue-600 dark:text-blue-300 border-gray-300 dark:border-base-300 rounded focus:ring-blue-500"
                checked={showConfidence}
                onChange={(e) => {
                  setShowConfidence(e.target.checked);
                  localStorage.setItem("formDefaults:showConfidence", String(e.target.checked));
                }}
              />
              <span className="text-xs text-base-content/60">Show AI Confidence</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-3.5 h-3.5 text-blue-600 dark:text-blue-300 border-gray-300 dark:border-base-300 rounded focus:ring-blue-500"
                checked={snap.snapEnabled}
                onChange={(e) => snap.setSnapEnabled(e.target.checked)}
              />
              <span className="text-xs text-base-content/60">Snap to guides</span>
            </label>
            <p className="text-xs text-base-content/40">
              Hold{" "}
              <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-base-200 border border-gray-300 dark:border-base-300 rounded text-[10px] font-mono">
                Space
              </kbd>{" "}
              to pan
            </p>
          </div>
        </div>

        {/* PDF Canvas */}
        <div className="flex-1 min-w-0 relative">
          {/* Floating bulk action bar */}
          <div
            className={cn(
              "absolute top-4 left-1/2 -translate-x-1/2 z-30 flex justify-center transition-all duration-200",
              selectedFieldIds.size > 1
                ? "opacity-100 translate-y-0 pointer-events-auto"
                : "opacity-0 -translate-y-2 pointer-events-none",
            )}
          >
            <div className="bg-base-100 shadow-lg border border-base-300 rounded-lg px-4 py-2 flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700 dark:text-base-content/80">
                {selectedFieldIds.size}{" "}
                {selectedFieldIds.size === 1 ? "field" : "fields"} selected
              </span>
              {alignment.canAlign && (
                <>
                  <div className="w-px h-5 bg-base-300" />
                  <button onClick={alignment.alignLeft} className="p-1.5 text-base-content/40 hover:text-blue-500 dark:hover:text-blue-300 transition-colors" title="Align left">
                    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="1" width="1.5" height="14" rx="0.5" /><rect x="4" y="3" width="10" height="3" rx="0.5" /><rect x="4" y="9" width="6" height="3" rx="0.5" /></svg>
                  </button>
                  <button onClick={alignment.alignCenterH} className="p-1.5 text-base-content/40 hover:text-blue-500 dark:hover:text-blue-300 transition-colors" title="Align center horizontally">
                    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor"><rect x="7.25" y="1" width="1.5" height="14" rx="0.5" /><rect x="2" y="3" width="12" height="3" rx="0.5" /><rect x="4" y="9" width="8" height="3" rx="0.5" /></svg>
                  </button>
                  <button onClick={alignment.alignRight} className="p-1.5 text-base-content/40 hover:text-blue-500 dark:hover:text-blue-300 transition-colors" title="Align right">
                    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor"><rect x="13.5" y="1" width="1.5" height="14" rx="0.5" /><rect x="2" y="3" width="10" height="3" rx="0.5" /><rect x="6" y="9" width="6" height="3" rx="0.5" /></svg>
                  </button>
                  <button onClick={alignment.alignTop} className="p-1.5 text-base-content/40 hover:text-blue-500 dark:hover:text-blue-300 transition-colors" title="Align top">
                    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="1" width="14" height="1.5" rx="0.5" /><rect x="3" y="4" width="3" height="10" rx="0.5" /><rect x="9" y="4" width="3" height="6" rx="0.5" /></svg>
                  </button>
                  <button onClick={alignment.alignCenterV} className="p-1.5 text-base-content/40 hover:text-blue-500 dark:hover:text-blue-300 transition-colors" title="Align center vertically">
                    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="7.25" width="14" height="1.5" rx="0.5" /><rect x="3" y="2" width="3" height="12" rx="0.5" /><rect x="9" y="4" width="3" height="8" rx="0.5" /></svg>
                  </button>
                  <button onClick={alignment.alignBottom} className="p-1.5 text-base-content/40 hover:text-blue-500 dark:hover:text-blue-300 transition-colors" title="Align bottom">
                    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="13.5" width="14" height="1.5" rx="0.5" /><rect x="3" y="2" width="3" height="10" rx="0.5" /><rect x="9" y="6" width="3" height="6" rx="0.5" /></svg>
                  </button>
                </>
              )}
              <div className="w-px h-5 bg-base-300" />
              <button
                onClick={() => {
                  setFieldValues((prev) => {
                    const next = { ...prev };
                    selectedFieldIds.forEach((id) => delete next[id]);
                    return next;
                  });
                  marquee.clearSelection();
                }}
                className="p-1.5 text-base-content/40 hover:text-red-500 transition-colors"
                title="Delete selected fields"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => marquee.clearSelection()}
                className="p-1.5 text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 transition-colors"
                title="Clear selection"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div
            ref={pdf.containerRef}
            tabIndex={-1}
            className={cn(
              "h-full overflow-auto bg-base-300 p-8 outline-none",
              pdf.panCursorClass
                ?? ((selectedTool === "text" || selectedTool === "checkbox")
                    ? "cursor-crosshair"
                    : undefined),
            )}
            onMouseDown={handleContainerMouseDown}
            onMouseMove={pdf.handleContainerMouseMove}
            onMouseUp={pdf.handleContainerMouseEnd}
            onMouseLeave={pdf.handleContainerMouseEnd}
          >
            <div
              ref={pdf.canvasRef}
              className={cn(
                "relative mx-auto bg-white shadow-xl select-none", /* paper — stays white in dark mode */
                (selectedTool === "text" || selectedTool === "checkbox")
                  ? "cursor-crosshair"
                  : pdf.panCursorClass || "cursor-default",
              )}
              style={{
                width: pdf.pdfDimensions.width * scale || 612,
                height: pdf.pdfDimensions.height * scale || 792,
                transition: pdf.pageReady ? "none" : "width 0.15s ease, height 0.15s ease",
              }}
              onMouseDown={handleCanvasMouseDown}
              onClick={handleCanvasClick}
            >
              {!pdfFileUrl ? (
                <div className="flex items-center justify-center h-96">
                  <span className="loading loading-spinner loading-md text-primary"></span>
                </div>
              ) : (
              <Document
                file={pdfFileUrl}
                onLoadSuccess={pdf.onDocumentLoadSuccess}
                loading={null}
                error={
                  <div className="flex items-center justify-center h-96 text-error">
                    Failed to load PDF
                  </div>
                }
              >
                {pdf.numPages > 0 && (
                  <div
                    className="transition-opacity duration-200"
                    style={{ opacity: pdf.pageReady ? 1 : 0 }}
                  >
                    <Page
                      key={`page-${pdf.currentPage}-${scale}`}
                      pageNumber={pdf.currentPage}
                      scale={scale}
                      onLoadSuccess={pdf.onPageLoadSuccess}
                      onRenderSuccess={() => pdf.setPageReady(true)}
                      loading={<span />}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </div>
                )}
              </Document>
              )}

              {/* Loading spinner during page transitions */}
              {!pdf.pageReady && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <span className="loading loading-spinner loading-md text-base-content/30" />
                </div>
              )}

              {/* Field overlays */}
              {pdf.pageReady &&
                currentPageFields.map((field) => {
                  const isSelected = selectedFieldIds.has(field.id);
                  const isEditing = editingFieldId === field.id;
                  const hasValue =
                    field.type === "circle"
                      ? hasCircleValue(field.value)
                      : field.value !== undefined &&
                        field.value !== null &&
                        field.value !== "" &&
                        field.value !== false;
                  const confidence = field.confidence;

                  const isTextType = field.type !== "checkbox" && field.type !== "circle";

                  const borderClass = isEditing
                    ? "border-blue-500 ring-2 ring-blue-400 z-20"
                    : isSelected
                      ? "border-blue-500 bg-blue-500/15 z-10 ring-2 ring-blue-300 cursor-move"
                      : showConfidence && confidence !== undefined
                        ? confidence >= 0.9
                          ? "border-green-400 hover:border-green-500"
                          : confidence >= 0.7
                            ? "border-yellow-400 hover:border-yellow-500"
                            : "border-red-400 hover:border-red-500"
                        : "border-primary/50 hover:border-primary cursor-pointer";

                  const bgClass = isEditing
                    ? "bg-white" // paper — stays white in dark mode
                    : isSelected
                      ? ""
                      : "bg-primary/10";

                  // Render field content
                  const renderContent = () => {
                    if (isEditing && isTextType) {
                      if (field.type === "textarea") {
                        return (
                          <textarea
                            className="w-full h-full bg-white text-gray-900 px-1 py-0.5 resize-none border-none outline-none"
                            style={{
                              fontSize: `${(field.fontSize || 9) * scale}px`,
                              lineHeight: field.lineHeight || 1.2,
                            }}
                            value={field.value ?? ""}
                            autoFocus
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            onDoubleClick={(e) => e.stopPropagation()}
                            onChange={(e) => updateValue(field.id, e.target.value)}
                            onBlur={() => setEditingFieldId(null)}
                            onKeyDown={(e) => {
                              if (e.key === "Escape") setEditingFieldId(null);
                            }}
                          />
                        );
                      }
                      return (
                        <input
                          type="text"
                          className="w-full h-full bg-white text-gray-900 px-1 border-none outline-none"
                          style={{
                            fontSize: `${(field.fontSize || 10) * scale}px`,
                          }}
                          value={field.value ?? ""}
                          autoFocus
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          onDoubleClick={(e) => e.stopPropagation()}
                          onChange={(e) => updateValue(field.id, e.target.value)}
                          onBlur={() => setEditingFieldId(null)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === "Escape")
                              setEditingFieldId(null);
                          }}
                        />
                      );
                    }

                    if (field.type === "circle") {
                      // Each printed word is a click target; the chosen ones get an ellipse.
                      return (
                        <>
                          {(field.optionRects || []).map((r, i) => {
                            const opt = field.options?.[i];
                            if (!r || opt === undefined) return null;
                            const on = isCircled(field.value, opt);
                            return (
                              <button
                                key={i}
                                type="button"
                                title={opt}
                                className={cn(
                                  "absolute rounded-[50%] border-2 transition-colors",
                                  on
                                    ? "border-blue-600 bg-blue-500/10"
                                    : "border-transparent hover:border-blue-400 hover:bg-blue-400/10",
                                )}
                                style={{
                                  left: (r.x - field.rect.x) * scale - 3,
                                  top: (r.y - field.rect.y) * scale - 3,
                                  width: r.width * scale + 6,
                                  height: r.height * scale + 6,
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateValue(field.id, toggleCircleOption(field, field.value, opt));
                                }}
                              />
                            );
                          })}
                        </>
                      );
                    }

                    if (field.type === "checkbox") {
                      return (
                        <div className="w-full h-full flex items-center justify-center">
                          <span
                            className={cn(
                              "font-bold",
                              hasValue && field.value ? "text-green-600" : "text-gray-300",
                            )}
                            style={{
                              fontSize: `${Math.min(field.rect.width, field.rect.height) * 0.7 * scale}px`,
                            }}
                          >
                            {field.value ? "\u2713" : ""}
                          </span>
                        </div>
                      );
                    }

                    if (field.type === "textarea") {
                      return (
                        <div
                          className="w-full h-full overflow-hidden px-1 py-0.5"
                          style={{
                            fontSize: `${(field.fontSize || 9) * scale}px`,
                            lineHeight: field.lineHeight || 1.2,
                          }}
                        >
                          <span
                            className={hasValue ? "text-gray-900" : "text-gray-300 italic"}
                            style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}
                          >
                            {hasValue ? String(field.value) : field.label}
                          </span>
                        </div>
                      );
                    }

                    return (
                      <div
                        className="w-full h-full flex items-center truncate px-1 overflow-hidden"
                        style={{
                          fontSize: `${(field.fontSize || 10) * scale}px`,
                        }}
                      >
                        <span className={hasValue ? "text-gray-900" : "text-gray-300 italic"}>
                          {hasValue ? String(field.value) : field.label}
                        </span>
                      </div>
                    );
                  };

                  // Use Rnd for selected fields (enables drag/resize)
                  if (isSelected) {
                    const rndHandlers = fieldMovement.getRndHandlers(field.id);

                    return (
                      <Rnd
                        key={field.id}
                        position={fieldMovement.getFieldPosition(field.id, field.rect)}
                        size={{
                          width: field.rect.width * scale,
                          height: field.rect.height * scale,
                        }}
                        enableResizing={selectedFieldIds.size === 1 && field.type !== "circle"}
                        disableDragging={field.type === "circle"}
                        resizeHandleStyles={{
                          top: { height: 4, top: -2 },
                          bottom: { height: 4, bottom: -2 },
                          left: { width: 4, left: -2 },
                          right: { width: 4, right: -2 },
                          topLeft: { width: 6, height: 6, top: -3, left: -3 },
                          topRight: { width: 6, height: 6, top: -3, right: -3 },
                          bottomLeft: { width: 6, height: 6, bottom: -3, left: -3 },
                          bottomRight: { width: 6, height: 6, bottom: -3, right: -3 },
                        }}
                        onDragStart={() => {
                          wasDraggedRef.current = false;
                          if (field.type !== "checkbox") setIsDraggingField(true);
                          rndHandlers.onDragStart();
                        }}
                        onDrag={(e: any, d: { x: number; y: number }) => {
                          wasDraggedRef.current = true;
                          if (!isDraggingField) setIsDraggingField(true);
                          rndHandlers.onDrag(e, d);
                        }}
                        onDragStop={(e: any, d: { x: number; y: number }) => {
                          setIsDraggingField(false);
                          rndHandlers.onDragStop(e, d);
                        }}
                        onResizeStart={() => { wasDraggedRef.current = true; }}
                        onResize={rndHandlers.onResize}
                        onResizeStop={(e: any, dir: any, ref: HTMLElement, delta: any, position: any) => {
                          rndHandlers.onResizeStop(e, dir, ref, delta, position);
                          if (field.type === "checkbox") {
                            localStorage.setItem("formDefaults:checkboxSize", String(Math.round(ref.offsetWidth / scale)));
                          }
                        }}
                        bounds="parent"
                        style={{ cursor: isDraggingField ? "move" : "pointer" }}
                        className={cn(
                          "border-2 rounded transition-colors",
                          borderClass,
                          bgClass,
                        )}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          if (field.type === "checkbox" && !wasDraggedRef.current) {
                            updateValue(field.id, !field.value);
                          } else if (!wasDraggedRef.current) {
                            marquee.handleFieldClick(field.id, e.shiftKey);
                          }
                          wasDraggedRef.current = false;
                        }}
                        onDoubleClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          if (field.type !== "checkbox" && field.type !== "circle") {
                            setEditingFieldId(field.id);
                          }
                        }}
                      >
                        {renderContent()}
                      </Rnd>
                    );
                  }

                  // Regular absolute div for non-selected fields
                  return (
                    <div
                      key={field.id}
                      className={cn(
                        "absolute border-2 rounded cursor-pointer transition-colors",
                        borderClass,
                        bgClass,
                      )}
                      style={{
                        left: field.rect.x * scale,
                        top: field.rect.y * scale,
                        width: field.rect.width * scale,
                        height: field.rect.height * scale,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        marquee.handleFieldClick(field.id, e.shiftKey);
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        if (field.type === "checkbox") {
                          updateValue(field.id, !field.value);
                        } else if (field.type === "circle") {
                          setSelectedFieldIds(new Set([field.id]));
                        } else {
                          setSelectedFieldIds(new Set([field.id]));
                          setEditingFieldId(field.id);
                        }
                      }}
                    >
                      {renderContent()}
                    </div>
                  );
                })}

              {/* Alignment snap guides (drawn while dragging a field) */}
              {pdf.pageReady && (
                <SnapGuideLines guides={snap.guides} scale={scale} />
              )}

              {/* Marquee selection rectangle */}
              {pdf.pageReady && marquee.marqueePreview && (
                <div
                  className="absolute border border-blue-500 bg-blue-500/10 pointer-events-none z-20"
                  style={marquee.marqueePreview}
                />
              )}
            </div>
          </div>

          <PdfFloatingNav
            currentPage={pdf.currentPage}
            numPages={pdf.numPages}
            scale={scale}
            onPrevPage={pdf.prevPage}
            onNextPage={pdf.nextPage}
            onZoomIn={pdf.zoomIn}
            onZoomOut={pdf.zoomOut}
            onZoomFit={pdf.zoomFit}
            minScale={0.25}
            maxScale={3}
          />
        </div>

        {/* Sidebar */}
        <div className="flex-shrink-0 flex flex-row">
          {/* Collapse/Expand toggle bar */}
          <button
            onClick={() => setSidebarCollapsed((v) => !v)}
            className="w-6 flex-shrink-0 border-l border-base-300 bg-base-200 hover:bg-blue-50 dark:hover:bg-blue-900/40 flex items-center justify-center cursor-pointer transition-colors group"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <span className="text-[10px] font-medium text-base-content/40 group-hover:text-blue-600 dark:group-hover:text-blue-300 tracking-wider uppercase [writing-mode:vertical-lr] rotate-180 select-none flex items-center gap-1">
              {sidebarCollapsed ? <ChevronRightIcon className="h-3 w-3" /> : <ChevronLeftIcon className="h-3 w-3" />}
              {sidebarCollapsed ? "Expand" : "Collapse"}
            </span>
          </button>
          {!sidebarCollapsed && (
          <div
            style={{ width: sidebarPanel.width }}
            className="relative shrink-0 bg-base-100 border-l border-base-300 flex flex-col overflow-hidden"
          >
            <ResizeHandle edge="left" active={sidebarPanel.isResizing} {...sidebarPanel.handleProps} />
          <div className="px-4 py-3 border-b border-base-300">
            <p className="text-xs font-medium text-base-content/40 uppercase tracking-wide">Fields</p>
          </div>
          {selectedFieldIds.size > 1 ? (
            <div className="p-4 space-y-4 overflow-auto flex-1">
              <p className="text-sm font-medium">
                <span className="text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/40 px-2 py-0.5 rounded">{selectedFieldIds.size}</span> <span className="text-gray-700 dark:text-base-content/80">selected</span>
              </p>
              <div className="pt-2 border-t border-base-300">
                <button
                  onClick={() => {
                    setFieldValues((prev) => {
                      const next = { ...prev };
                      selectedFieldIds.forEach((id) => delete next[id]);
                      return next;
                    });
                    marquee.clearSelection();
                  }}
                  className="w-full py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-lg transition-colors flex items-center justify-center gap-1"
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete {selectedFieldIds.size} Fields
                </button>
              </div>
            </div>
          ) : selectedField ? (
            <div className="p-4 space-y-4 overflow-auto flex-1">
              <div>
                <p className="font-medium text-sm text-base-content">
                  {selectedField.label || selectedField.name}
                </p>
                <p className="text-xs text-base-content/40 mt-0.5">
                  {selectedField.section || "No section"} &middot;{" "}
                  {FIELD_TYPE_LABELS[selectedField.type] || selectedField.type}
                </p>
              </div>

              {selectedField.confidence !== undefined && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-base-content/60">AI Confidence:</span>
                  <span
                    className={cn("px-2 py-0.5 rounded text-xs font-medium", {
                      "bg-green-100 dark:bg-green-950/40 text-green-800 dark:text-green-400": selectedField.confidence >= 0.9,
                      "bg-yellow-100 dark:bg-yellow-950/40 text-yellow-800 dark:text-amber-400":
                        selectedField.confidence >= 0.7 && selectedField.confidence < 0.9,
                      "bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-400": selectedField.confidence < 0.7,
                    })}
                  >
                    {Math.round(selectedField.confidence * 100)}%
                  </span>
                </div>
              )}

              {(selectedField.type === "text" || selectedField.type === "textarea") && (
                <div className="form-control">
                  <label className="cursor-pointer flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 dark:text-blue-300 border-gray-300 dark:border-base-300 rounded focus:ring-blue-500"
                      checked={selectedField.type === "textarea"}
                      onChange={(e) => {
                        const newType = e.target.checked ? "textarea" : "text";
                        localStorage.setItem("formDefaults:multiline", String(e.target.checked));
                        setFieldValues((prev) => ({
                          ...prev,
                          [selectedField.id]: { ...prev[selectedField.id], type: newType },
                        }));
                      }}
                    />
                    <span className="text-sm text-gray-700 dark:text-base-content/80">Multi-line</span>
                  </label>
                </div>
              )}

              <div className="form-control">
                <span className="font-medium text-sm pb-1">Value</span>
                {selectedField.type === "circle" ? (
                  <div className="space-y-1">
                    <p className="text-xs text-base-content/50 m-0">
                      {selectedField.multiple ? "Circle all that apply" : "Circle one"}
                    </p>
                    {(selectedField.options || []).map((opt, i) => (
                      <label key={i} className="cursor-pointer flex items-center gap-2">
                        <input
                          type={selectedField.multiple ? "checkbox" : "radio"}
                          className="w-4 h-4"
                          checked={isCircled(selectedField.value, opt)}
                          onChange={() =>
                            updateValue(selectedField.id, toggleCircleOption(selectedField, selectedField.value, opt))
                          }
                        />
                        <span className="text-sm text-gray-700 dark:text-base-content/80">{opt}</span>
                      </label>
                    ))}
                  </div>
                ) : selectedField.type === "checkbox" ? (
                  <label className="cursor-pointer flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 dark:text-blue-300 border-gray-300 dark:border-base-300 rounded focus:ring-blue-500"
                      checked={!!selectedField.value}
                      onChange={(e) => updateValue(selectedField.id, e.target.checked)}
                    />
                    <span className="text-sm text-gray-700 dark:text-base-content/80">
                      {selectedField.value ? "Checked" : "Unchecked"}
                    </span>
                  </label>
                ) : selectedField.type === "textarea" ? (
                  <textarea
                    className="w-full h-32 px-3 py-2 text-sm border border-base-300 rounded-md bg-base-100 focus:outline-none focus:border-blue-400 transition-colors resize-y"
                    value={selectedField.value || ""}
                    onChange={(e) => updateValue(selectedField.id, e.target.value)}
                    placeholder={`Enter ${selectedField.label || "value"}...`}
                  />
                ) : (
                  <input
                    type="text"
                    className="w-full h-8 px-3 text-sm border border-base-300 rounded-md bg-base-100 focus:outline-none focus:border-blue-400 transition-colors"
                    value={selectedField.value || ""}
                    onChange={(e) => updateValue(selectedField.id, e.target.value)}
                    placeholder={`Enter ${selectedField.label || "value"}...`}
                  />
                )}
              </div>

              {selectedField.type !== "checkbox" && selectedField.type !== "circle" && (
                <div className="form-control">
                  <span className="font-medium text-sm pb-1">Font Size</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        const current = selectedField.fontSize || (selectedField.type === "textarea" ? 9 : 10);
                        if (current > 4) {
                          const next = current - 1;
                          setFieldValues((prev) => ({
                            ...prev,
                            [selectedField.id]: { ...prev[selectedField.id], fontSize: next },
                          }));
                          localStorage.setItem("formDefaults:fontSize", String(next));
                        }
                      }}
                      className="w-7 h-7 flex items-center justify-center border border-base-300 rounded text-gray-600 dark:text-base-content/70 hover:bg-base-200 transition-colors text-sm font-medium"
                    >
                      -
                    </button>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="w-14 h-7 px-2 text-sm text-center border border-base-300 rounded-md bg-base-100 focus:outline-none focus:border-blue-400 transition-colors [appearance:textfield]"
                      value={selectedField.fontSize || (selectedField.type === "textarea" ? 9 : 10)}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 4 && val <= 72) {
                          setFieldValues((prev) => ({
                            ...prev,
                            [selectedField.id]: { ...prev[selectedField.id], fontSize: val },
                          }));
                          localStorage.setItem("formDefaults:fontSize", String(val));
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        const current = selectedField.fontSize || (selectedField.type === "textarea" ? 9 : 10);
                        if (current < 72) {
                          const next = current + 1;
                          setFieldValues((prev) => ({
                            ...prev,
                            [selectedField.id]: { ...prev[selectedField.id], fontSize: next },
                          }));
                          localStorage.setItem("formDefaults:fontSize", String(next));
                        }
                      }}
                      className="w-7 h-7 flex items-center justify-center border border-base-300 rounded text-gray-600 dark:text-base-content/70 hover:bg-base-200 transition-colors text-sm font-medium"
                    >
                      +
                    </button>
                    <span className="text-xs text-base-content/40">px</span>
                  </div>
                </div>
              )}

              {selectedField.type === "textarea" && (
                <div className="form-control">
                  <span className="font-medium text-sm pb-1">Line Spacing</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        const current = selectedField.lineHeight || 1.2;
                        const next = Math.round((current - 0.1) * 10) / 10;
                        if (next >= 0.8) {
                          setFieldValues((prev) => ({
                            ...prev,
                            [selectedField.id]: { ...prev[selectedField.id], lineHeight: next },
                          }));
                          localStorage.setItem("formDefaults:lineHeight", String(next));
                        }
                      }}
                      className="w-7 h-7 flex items-center justify-center border border-base-300 rounded text-gray-600 dark:text-base-content/70 hover:bg-base-200 transition-colors text-sm font-medium"
                    >
                      -
                    </button>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-14 h-7 px-2 text-sm text-center border border-base-300 rounded-md bg-base-100 focus:outline-none focus:border-blue-400 transition-colors [appearance:textfield]"
                      value={selectedField.lineHeight || 1.2}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        if (!isNaN(val) && val >= 0.8 && val <= 3) {
                          setFieldValues((prev) => ({
                            ...prev,
                            [selectedField.id]: { ...prev[selectedField.id], lineHeight: val },
                          }));
                          localStorage.setItem("formDefaults:lineHeight", String(val));
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        const current = selectedField.lineHeight || 1.2;
                        const next = Math.round((current + 0.1) * 10) / 10;
                        if (next <= 3) {
                          setFieldValues((prev) => ({
                            ...prev,
                            [selectedField.id]: { ...prev[selectedField.id], lineHeight: next },
                          }));
                          localStorage.setItem("formDefaults:lineHeight", String(next));
                        }
                      }}
                      className="w-7 h-7 flex items-center justify-center border border-base-300 rounded text-gray-600 dark:text-base-content/70 hover:bg-base-200 transition-colors text-sm font-medium"
                    >
                      +
                    </button>
                    <span className="text-xs text-base-content/40">×</span>
                  </div>
                </div>
              )}

              {selectedField.additionalContext && (
                <div className="text-xs text-base-content/40 bg-base-200 rounded p-2">
                  <span className="font-medium">AI Context:</span>{" "}
                  {selectedField.additionalContext}
                </div>
              )}

              <div className="pt-2 border-t border-base-300 flex items-center gap-3">
                <button
                  onClick={() =>
                    updateValue(
                      selectedField.id,
                      selectedField.type === "checkbox" ? false : selectedField.type === "circle" && selectedField.multiple ? [] : "",
                    )
                  }
                  className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors"
                >
                  Clear value
                </button>
                <button
                  onClick={() => handleDeleteField(selectedField.id)}
                  className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors flex items-center gap-1"
                >
                  <TrashIcon className="h-3 w-3" />
                  Delete field
                </button>
              </div>
            </div>
          ) : (
            <div className="p-4 text-center text-base-content/40">
              <p>Select a field to edit its value</p>
            </div>
          )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormFiller;
